// Simple UI Performance Configuration
// Toggle the most expensive UI effects for performance testing

export interface UIPerformanceConfig {
  // Most expensive effects - these have the biggest performance impact
  disableInfiniteAnimations: boolean;  // Ellipse animations, breathing effects
  disableGlowEffects: boolean;         // Box-shadow glows, text-shadow effects  
  disableBlurEffects: boolean;         // Backdrop filters, blur effects
  disableCosmicBackgrounds: boolean;   // Complex gradient backgrounds
}

// Default configuration - all effects enabled (full visual experience)
const DEFAULT_CONFIG: UIPerformanceConfig = {
  disableInfiniteAnimations: false,
  disableGlowEffects: false,
  disableBlurEffects: false,
  disableCosmicBackgrounds: false,
};

// Performance configuration - disable expensive effects
const PERFORMANCE_CONFIG: UIPerformanceConfig = {
  disableInfiniteAnimations: true,   // Biggest performance impact
  disableGlowEffects: true,          // Medium-high impact  
  disableBlurEffects: true,          // Medium impact
  disableCosmicBackgrounds: false,   // Keep for visual appeal
};

// Current active configuration
let currentConfig = { ...DEFAULT_CONFIG };

// Simple API for controlling UI performance
export const UIPerformance = {
  // Enable performance mode (disable expensive effects)
  enablePerformanceMode: () => {
    currentConfig = { ...PERFORMANCE_CONFIG };
    //console.log('🚀 UI Performance Mode enabled - expensive effects disabled');
  },

  // Enable full effects (default beautiful mode)
  enableFullEffects: () => {
    currentConfig = { ...DEFAULT_CONFIG };
    //console.log('✨ Full UI Effects enabled - maximum visual quality');
  },

  // Get current configuration
  getConfig: () => currentConfig,

  // Toggle individual effects
  toggleInfiniteAnimations: () => {
    currentConfig.disableInfiniteAnimations = !currentConfig.disableInfiniteAnimations;
    //console.log('🔄 Infinite animations:', currentConfig.disableInfiniteAnimations ? 'DISABLED' : 'ENABLED');
  },

  toggleGlowEffects: () => {
    currentConfig.disableGlowEffects = !currentConfig.disableGlowEffects;
    //console.log('💫 Glow effects:', currentConfig.disableGlowEffects ? 'DISABLED' : 'ENABLED');
  },

  toggleBlurEffects: () => {
    currentConfig.disableBlurEffects = !currentConfig.disableBlurEffects;
    //console.log('🌫️ Blur effects:', currentConfig.disableBlurEffects ? 'DISABLED' : 'ENABLED');
  },

  toggleCosmicBackgrounds: () => {
    currentConfig.disableCosmicBackgrounds = !currentConfig.disableCosmicBackgrounds;
    //console.log('🌌 Cosmic backgrounds:', currentConfig.disableCosmicBackgrounds ? 'DISABLED' : 'ENABLED');
  },

  // Helper function to check if an effect should be disabled
  shouldDisable: (effect: keyof UIPerformanceConfig): boolean => {
    return currentConfig[effect];
  }
};

// Export for easy console access during development
if (typeof window !== 'undefined') {
  (window as any).UIPerformance = UIPerformance;
} 